import * as React from "react";
import User from "../../../model/user/User";
import OrganisationUsersTableRow from "./OrganisationUsersTableRow";
import {PartnerBrandSearch} from "../../../model/partnerBrand/PartnerBrand";
import DataGrid from "../../../hiberUiKit/src/organisms/DataGrid/DataGrid";
import IconWrapper from "../../../hiberUiKit/src/atoms/Icon/IconWrapper";
import {Icon} from "../../../hiberUiKit/src/atoms/Icon/Icon";
import ButtonSecondary from "../../../hiberUiKit/src/atoms/Buttons/ButtonSecondary";
import "../../v5/table/workspaceCardList.scss";

interface ComponentProps {
	users: User[];
	partnerBrands: { [key: string]: PartnerBrandSearch };
	sort: { field: string; direction: 'asc' | 'desc' };
	onSortChange: (field: string) => void;
	loading: boolean;
	onRowClick: (user: User) => void;
	onClearFilters?: () => void;
}

interface State {
	/**
	 * Indicates if the screen is a phone's, where users show as cards rather than as a table.
	 */
	isPhone: boolean;
}

export default class OrganisationUsersTable extends React.Component<ComponentProps, State> {
	phoneQuery: MediaQueryList | null = window.matchMedia ? window.matchMedia("(max-width: 640px)") : null;

	constructor(props: ComponentProps) {
		super(props);
		this.state = {isPhone: !!this.phoneQuery && this.phoneQuery.matches};
	}

	componentDidMount() {
		if (this.phoneQuery) {
			this.phoneQuery.addListener(this.onPhoneChange);
		}
	}

	componentWillUnmount() {
		if (this.phoneQuery) {
			this.phoneQuery.removeListener(this.onPhoneChange);
		}
	}

	onPhoneChange = (query: MediaQueryList) => this.setState({isPhone: query.matches});

	/**
	 * A card per user on a phone, in the look of the workspace lists: the name, the email and the partner brand,
	 * which is what anyone looking for a user there needs to tell them apart.
	 */
	private renderCards() {
		const {users, partnerBrands, loading, onRowClick} = this.props;
		if (loading) {
			return null;
		}
		if (!users.length) {
			return <div className={"c-workspace-cards_empty"}>No users match the current filters.</div>;
		}
		return (
			<ul className={"c-workspace-cards"}>
				{users.map(user => {
					const partnerBrand = partnerBrands[user.data.partnerBrandId.orNull];
					return (
						<li key={user.id}>
							{/* A disabled user, listed with "Show disabled" on, is greyed out and says so on the right. */}
							<button type={"button"}
									className={`c-workspace-cards_card${user.disabled ? " is-disabled" : " is-single-column"}`}
									onClick={() => onRowClick(user)}>
								<span className={"c-workspace-cards_main"}>
									<span className={"c-workspace-cards_title"}>
										{`${user.data.firstName} ${user.data.lastName}`}
									</span>
									<span className={"c-workspace-cards_line is-brand"}>{user.data.username}</span>
									{partnerBrand &&
									<span className={"c-workspace-cards_line is-secondary"}>{partnerBrand.name}</span>}
								</span>
								{user.disabled &&
								<span className={"c-workspace-cards_aside"}>
									<span className={"c-workspace-cards_status"}>Disabled</span>
								</span>}
							</button>
						</li>
					);
				})}
			</ul>
		);
	}

	private renderSortHeader(label: string, field: string) {
		const {sort, onSortChange} = this.props;
		const active = sort.field === field;
		return (
			<th className={`sortable-header${active ? ' is-sorted' : ''}`}
				style={{cursor: 'pointer', userSelect: 'none'}}
				onClick={() => onSortChange(field)}>
				<span style={{display: 'inline-flex', alignItems: 'center', gap: '4px'}}>
					{label}
					{active && (
						<IconWrapper
							variant={"lighterBlue"}
							icon={sort.direction === 'asc' ? Icon.ArrowUp : Icon.ArrowDown}
							size={11}
						/>
					)}
				</span>
			</th>
		);
	}

	render() {
		if (this.state.isPhone) {
			return this.renderCards();
		}
		const {users, partnerBrands, loading, onRowClick, sort, onSortChange} = this.props;

		return (
			<div className="o-data-grid-fixed-wrapper">
			<DataGrid hasHover={true} hasPointer={true}>
				<colgroup>
					<col style={{width: '17%'}}/>
					<col style={{width: '10%'}}/>
					<col style={{width: '11%'}}/>
					<col style={{width: '18%'}}/>
					<col style={{width: '15%'}}/>
					<col style={{width: '19%'}}/>
					<col style={{width: '10%'}}/>
				</colgroup>
				<thead>
				<tr>
					<th>Id</th>
					{this.renderSortHeader('First Name', 'firstName')}
					{this.renderSortHeader('Last Name', 'lastName')}
					{this.renderSortHeader('Username', 'email')}
					<th>Partner Brand</th>
					<th>Roles</th>
					<th className={`sortable-header${sort.field === 'disabled' ? ' is-sorted' : ''}`}
						style={{cursor: 'pointer', userSelect: 'none'}}
						onClick={() => onSortChange('disabled')}>
						<span style={{display: 'inline-flex', alignItems: 'center', gap: '4px'}}>
							Status
							{sort.field === 'disabled' && (
								<IconWrapper
									variant={"lighterBlue"}
									icon={sort.direction === 'asc' ? Icon.ArrowUp : Icon.ArrowDown}
									size={11}
								/>
							)}
						</span>
					</th>
				</tr>
				</thead>
				<tbody>
				{loading ? (
					<tr>
						<td colSpan={7} className="text-center py-3">
							<span className="spinner-border spinner-border-sm"/> Loading…
						</td>
					</tr>
				) : users.length === 0 ? (
					<tr>
						<td colSpan={7}>
							<div style={{textAlign: 'center', padding: '48px 0', color: '#112363'}}>
								<IconWrapper icon={Icon.Filter} variant={"blue_medium"} size={32}/>
								<p style={{margin: '12px 0 20px', fontSize: '14px', fontWeight: 500, color: '#112363'}}>
									No users match the current filters.
								</p>
								{this.props.onClearFilters && (
									<ButtonSecondary label="Clear filters" onClick={this.props.onClearFilters}/>
								)}
							</div>
						</td>
					</tr>
				) : (
					users.map((user, index) => (
						<OrganisationUsersTableRow
							user={user}
							key={index}
							onClick={onRowClick}
							partnerBrands={partnerBrands}
						/>
					))
				)}
				</tbody>
			</DataGrid>
			</div>
		);
	}
}